import { useAPI } from "@/services/api";
import { useAnalytics } from "@Light/app/analytics";
import { EnrollHelpButton } from "@Light/components/enroll/EnrollHelpButtonFooter";
import { PartnerTosDisclaimer } from "@Light/components/enroll/PartnerTosDisclaimer";
import { ButtonFooter } from "@Light/components/page/ButtonFooter";
import {
  useHeaderCompact,
  useHeaderFloating,
  useHeaderTitle,
} from "@Light/components/page/header/context";
import { MutationButton } from "@Light/components/page/MutationButton";
import { PageBody } from "@Light/components/page/PageBody";
import { HeaderSplashImage } from "@Light/components/page/SplashImage";
import { Trust } from "@Light/components/rateOffer/Trust";
import { useIsSubtleBrand, useScaffold } from "@Light/scaffold";
import { EMAIL_PATTERN } from "@Light/utils/email";
import { clsx } from "clsx";
import { useForm, useWatch } from "react-hook-form";
import { useConfiguration, useFeatureFlag } from "../WithConfiguration";

const SIGN_IN_TEXT =
  "Sign in to view your electricity plan offer or manage an existing plan";

export function Login() {
  const { useLoginEmailTokenMutation, useSignupMutation } = useAPI();
  const [loginEmailToken, loginEmailTokenState] = useLoginEmailTokenMutation();
  const [signup, signupState] = useSignupMutation();
  const { planUUID, attribution } = useAnalytics();
  const allowSignups = useFeatureFlag("CUSTOMER_CLIENT_ALLOW_SIGNUPS");
  const configuration = useConfiguration();

  const scaffold = useScaffold();
  const Field = scaffold.system.field;
  const Label = scaffold.system.label;
  const Input = scaffold.system.input;
  const ErrorMessage = scaffold.system.errorMessage;
  const {
    register,
    handleSubmit,
    control,
    clearErrors,
    formState: { errors },
  } = useForm<{ email: string }>({ reValidateMode: "onSubmit" });
  const email = useWatch({ control, name: "email" });

  const isSubtle = useIsSubtleBrand();
  const brandTitle = `${scaffold.page.companyName} Electricity Plans`;
  const logo = configuration?.logo;
  const showSplashImage = scaffold.page.splashImage && !isSubtle;

  useHeaderFloating(!isSubtle);
  useHeaderCompact(isSubtle);
  useHeaderTitle(isSubtle ? brandTitle : undefined);

  const onSubmit = handleSubmit(({ email }) => {
    loginEmailToken({ email });
  });

  const onSignup = handleSubmit(({ email }) => {
    signup({ email, plan: planUUID, attribution });
  });

  const Navigate = scaffold.system.navigate;
  if (loginEmailTokenState.isSuccess) {
    const params = new URLSearchParams({ email });
    return <Navigate to={`/login/check-email?${params}`} />;
  }
  if (signupState.isSuccess) {
    const params = new URLSearchParams({ email, signup: "true" });
    return <Navigate to={`/login/check-email?${params}`} />;
  }

  const makeHeadingProps = () => {
    if (isSubtle) {
      return { title: SIGN_IN_TEXT };
    }
    const subtitle = `${SIGN_IN_TEXT}.`;
    if (logo) {
      return {
        icon: (
          <div className="flex justify-center">
            <img
              className="h-full max-h-16 w-auto"
              src={logo}
              alt={scaffold.page.companyName}
            />
          </div>
        ),
        subtitle,
      };
    }
    return { title: brandTitle, subtitle };
  };

  return (
    <form
      onSubmit={onSubmit}
      noValidate
      className="flex flex-col h-full overflow-y-auto"
    >
      {showSplashImage && <HeaderSplashImage />}
      <PageBody
        className={clsx("flex-1 min-h-fit", showSplashImage && "max-sm:pt-4xl")}
        {...makeHeadingProps()}
        size="lg"
        centered
        centerText={!isSubtle}
        footer={<Trust size="md" />}
      >
        <div className="flex flex-col gap-3xl">
          <Field className="w-full">
            <Label>Email</Label>
            <Input
              type="email"
              isError={!!errors.email}
              {...register("email", {
                required: true,
                onChange: () => {
                  clearErrors("email");
                },
                pattern: {
                  value: EMAIL_PATTERN,
                  message: "Please enter a valid email address.",
                },
              })}
            />
            <ErrorMessage error={errors.email?.message} />
            <PartnerTosDisclaimer className="mt-sm text-wrap" />
          </Field>
          <ButtonFooter>
            <MutationButton
              mutation={loginEmailTokenState}
              mutateButtonText="Send login email"
              errorMessage="We encountered an error while trying to log you in. Please try again later."
            />
            {allowSignups && (
              <MutationButton
                type="button"
                buttonType="outline"
                mutation={signupState}
                mutate={onSignup}
                mutateButtonText="Create an account"
                errorMessage="We encountered an error while trying to create your account. Please try again later."
              />
            )}
            <EnrollHelpButton size="md" eventVariant="login" />
          </ButtonFooter>
        </div>
      </PageBody>
    </form>
  );
}
